import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";

class FieldReportingDashboardStore {
  // Observables
  isLoading = false;
  error: string | null = null;
  panelStats = {
    totalPanels: 0,
    installedPanels: 0,
    inProgressPanels: 0,
  };
  hardwareStats = {
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
  };
  recentActivities: any[] = [];
  jobSummaries: any[] = [];

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
  }

  // Actions
  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setError(error: string | null) {
    this.error = error;
  }

  // Async actions
  async loadDashboardData() {
    this.setLoading(true);
    this.setError(null);

    try {
      // Load panels data
      await (this.rootStore.panelStore as ServiceStore).find({ query: { $limit: 1000 } });
      
      // Load field activities
      await (this.rootStore.fieldActivityStore as ServiceStore).find({ 
        query: { 
          $limit: 10,
          $sort: { activityDate: -1, activityTime: -1 }
        } 
      });

      // Load hardware orders
      await (this.rootStore.hardwareOrdersStore as ServiceStore).find({ query: { $limit: 1000 } });

      runInAction(() => {
        // Calculate panel statistics
        const panels = (this.rootStore.panelStore as ServiceStore).entities;
        this.panelStats.totalPanels = panels.length;
        this.panelStats.installedPanels = panels.filter(
          (p: any) => p.installationDate && p.installationDate !== "1970-01-01"
        ).length;
        this.panelStats.inProgressPanels = panels.filter(
          (p: any) => p.castingDate && p.castingDate !== "1970-01-01" && 
                     (!p.installationDate || p.installationDate === "1970-01-01")
        ).length;

        // Calculate hardware statistics
        const hardwareOrders = (this.rootStore.hardwareOrdersStore as ServiceStore).entities;
        this.hardwareStats.totalOrders = hardwareOrders.length;
        this.hardwareStats.pendingOrders = hardwareOrders.filter(
          (h: any) => h.status === "pending" || !h.status
        ).length;
        this.hardwareStats.completedOrders = hardwareOrders.filter(
          (h: any) => h.status === "completed"
        ).length;

        // Set recent activities
        this.recentActivities = (this.rootStore.fieldActivityStore as ServiceStore).entities.slice(0, 10);

        // Calculate job summaries
        const jobMap = new Map();
        
        // Count panels per job
        panels.forEach((panel: any) => {
          const jobNumber = panel.jobNumber;
          if (!jobMap.has(jobNumber)) {
            jobMap.set(jobNumber, {
              jobNumber,
              panelCount: 0,
              hardwareOrderCount: 0,
              activityCount: 0,
            });
          }
          jobMap.get(jobNumber).panelCount++;
        });

        // Count hardware orders per job
        hardwareOrders.forEach((order: any) => {
          const jobNumber = order.jobNumber;
          if (jobNumber) {
            if (!jobMap.has(jobNumber)) {
              jobMap.set(jobNumber, {
                jobNumber,
                panelCount: 0,
                hardwareOrderCount: 0,
                activityCount: 0,
              });
            }
            jobMap.get(jobNumber).hardwareOrderCount++;
          }
        });

        // Count activities per job
        (this.rootStore.fieldActivityStore as ServiceStore).entities.forEach((activity: any) => {
          const jobNumber = activity.jobNumber;
          if (!jobMap.has(jobNumber)) {
            jobMap.set(jobNumber, {
              jobNumber,
              panelCount: 0,
              hardwareOrderCount: 0,
              activityCount: 0,
            });
          }
          jobMap.get(jobNumber).activityCount++;
        });

        this.jobSummaries = Array.from(jobMap.values())
          .sort((a, b) => b.panelCount - a.panelCount)
          .slice(0, 8);
      });
    } catch (error) {
      console.error("Error loading dashboard data:", error);
      runInAction(() => {
        this.setError("Failed to load dashboard data");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  // Computed values
  get isDataLoaded() {
    return !this.isLoading && !this.error;
  }
}

export default FieldReportingDashboardStore;